<template>
    <div class="import-content">
        <div class="import-header">
            <h6 class="section-title">Импорт холодных клиентов</h6>
            <p class="text-muted">Загрузите файл с данными для автоматического создания клиентов (тестил CSV, UTF-8, разделитель - запятая)</p>
        </div>

        <!-- Выбор платформы -->
        <div class="form-section">
            <h6 class="section-title">Источник данных</h6>
            <b-form-group>
                <b-form-radio-group
                    v-model="selectedPlatform"
                    :options="platformOptions"
                    stacked
                ></b-form-radio-group>
            </b-form-group>
        </div>

        <!-- Загрузка файлов -->
        <div class="form-section">
            <h6 class="section-title">Загрузка файлов</h6>
            <b-form-group>
                <b-form-file
                    v-model="selectedFiles"
                    :accept="acceptedFileTypes"
                    placeholder="Выберите файлы для загрузки"
                    drop-placeholder="Перетащите файлы сюда"
                    multiple
                    @change="onFilesSelected"
                ></b-form-file>
                <small class="form-text text-muted">
                    Поддерживаемые форматы: {{ acceptedFileTypes }}<br>
                    <strong>Примечание:</strong> Большие файлы будут автоматически разбиты на части по 10 МБ<br>
                    Предварительный просмотр доступен только для CSV файлов
                </small>
            </b-form-group>

            <!-- Список выбранных файлов -->
            <div v-if="fileList.length > 0" class="file-list">
                <h6>Выбранные файлы:</h6>
                <div class="file-item" v-for="(file, index) in fileList" :key="index">
                    <div class="file-info">
                        <span class="file-name">{{ file.name }}</span>
                        <span class="file-size">({{ formatFileSize(file.size) }})</span>
                        <span v-if="file.chunks > 1" class="file-chunks">{{ file.chunks }} частей</span>
                    </div>
                    <div class="file-status">
                        <b-badge v-if="file.status === 'pending'" variant="secondary">Ожидает</b-badge>
                        <b-badge v-else-if="file.status === 'uploading'" variant="primary">Загружается</b-badge>
                        <b-badge v-else-if="file.status === 'completed'" variant="success">Завершен</b-badge>
                        <b-badge v-else-if="file.status === 'error'" variant="danger">Ошибка</b-badge>
                    </div>
                </div>
            </div>

            <!-- Предварительный просмотр -->
            <div v-if="previewData.length > 0" class="preview-section">
                <h6>Предварительный просмотр (первые 5 записей):</h6>
                <div class="table-responsive">
                    <table class="table table-sm table-striped">
                        <thead>
                            <tr>
                                <th v-for="column in previewColumns" :key="column">
                                    {{ getColumnLabel(column) }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, index) in previewData" :key="index">
                                <td v-for="column in previewColumns" :key="column">
                                    {{ row[column] || '-' }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- Настройки импорта -->
        <div class="form-section">
            <h6 class="section-title">Настройки импорта</h6>
            
            <!-- Маппинг полей -->
            <div class="field-mapping">
                <h6>Соответствие полей:</h6>
                <div v-for="(field, key) in fieldMapping" :key="key" class="row mb-2">
                    <div class="col-md-4">
                        <label class="form-label">{{ field.label }}</label>
                    </div>
                    <div class="col-md-8">
                        <b-form-select
                            v-model="fieldMapping[key].sourceColumn"
                            :options="columnOptions"
                            size="sm"
                        >
                            <template #first>
                                <b-form-select-option :value="null" disabled>
                                    Выберите колонку
                                </b-form-select-option>
                            </template>
                        </b-form-select>
                    </div>
                </div>
            </div>

            <!-- Дополнительные настройки -->
            <div class="import-settings">
                <b-form-checkbox v-model="skipDuplicates">
                    Пропускать дубликаты (по ИНН)
                </b-form-checkbox>
                <b-form-checkbox v-model="validateData">
                    Валидировать данные перед импортом
                </b-form-checkbox>
            </div>
        </div>

        <!-- Прогресс импорта -->
        <div v-if="importProgress.show" class="form-section">
            <h6 class="section-title">Прогресс импорта</h6>
            <div class="progress mb-2">
                <div 
                    class="progress-bar" 
                    :class="importProgress.variant"
                    :style="{ width: importProgress.percentage + '%' }"
                >
                    {{ importProgress.percentage }}%
                </div>
            </div>
            <p class="text-muted">{{ importProgress.message }}</p>
            
            <!-- Детальный прогресс по файлам -->
            <div v-if="fileList.length > 1" class="files-progress">
                <h6>Прогресс по файлам:</h6>
                <div class="file-progress-item" v-for="(file, index) in fileList" :key="index">
                    <div class="file-progress-info">
                        <span class="file-name">{{ file.name }}</span>
                        <span class="file-progress-text">{{ file.progressText || 'Ожидает' }}</span>
                    </div>
                    <div class="progress file-progress-bar">
                        <div 
                            class="progress-bar" 
                            :class="getFileProgressVariant(file.status)"
                            :style="{ width: (file.progress || 0) + '%' }"
                        >
                            {{ file.progress || 0 }}%
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Результаты импорта -->
        <div v-if="importResults" class="form-section">
            <h6 class="section-title">Результаты импорта</h6>
            <div class="alert" :class="importResults.alertClass">
                <h6>{{ importResults.title }}</h6>
                                 <ul class="mb-0">
                     <li>Обработано записей: {{ importResults.total }}</li>
                     <li>Успешно импортировано: {{ importResults.success }}</li>
                     <li>Ошибок: {{ importResults.errors }}</li>
                     <li v-if="importResults.skipped > 0">Пропущено дубликатов: {{ importResults.skipped }}</li>
                     <li v-if="importResults.filtered > 0">Отфильтровано (без контактов): {{ importResults.filtered }}</li>
                 </ul>
            </div>
        </div>

        <!-- Действия -->
        <div class="form-actions-bottom">
            <b-button
                variant="secondary"
                @click="$emit('input', false)"
                class="mr-2"
                :disabled="importProgress.show"
            >
                Закрыть
            </b-button>
            <b-button
                v-if="importProgress.show"
                variant="warning"
                @click="cancelImport"
                class="mr-2"
            >
                Отменить импорт
            </b-button>
            <b-button
                variant="primary"
                @click="startImport"
                :disabled="!canStartImport"
                :loading="importProgress.show"
            >
                {{ importProgress.show ? 'Импорт...' : 'Начать импорт' }}
            </b-button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ColdClientImportModal',
    props: {
        value: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedPlatform: '2gis',
            selectedFiles: null,
            fileList: [],
            previewData: [],
            previewColumns: [],
            importProgress: {
                show: false,
                percentage: 0,
                message: '',
                variant: 'bg-primary'
            },
            importResults: null,
            skipDuplicates: true,
            validateData: true,
            isImportCancelled: false,
            currentFileIndex: 0,
            fieldMapping: {
                company_name: {
                    label: 'Название компании',
                    sourceColumn: null,
                    required: true
                },
                inn: {
                    label: 'ИНН',
                    sourceColumn: null,
                    required: false
                },
                phone: {
                    label: 'Телефон',
                    sourceColumn: null,
                    required: false
                },
                email: {
                    label: 'Email',
                    sourceColumn: null,
                    required: false
                },
                website: {
                    label: 'Сайт',
                    sourceColumn: null,
                    required: false
                },
                address: {
                    label: 'Адрес',
                    sourceColumn: null,
                    required: false
                },
                city_name: {
                    label: 'Город',
                    sourceColumn: null,
                    required: true
                },
                industry: {
                    label: 'Отрасль',
                    sourceColumn: null,
                    required: false
                }
            }
        }
    },
    computed: {
        platformOptions() {
            return [
                { value: '2gis', text: '2GIS' },
            ]
        },
        acceptedFileTypes() {
            return '.csv,.xlsx,.xls'
        },
        columnOptions() {
            return this.previewColumns.map(col => ({
                value: col,
                text: this.getColumnLabel(col)
            }))
        },
        canStartImport() {
            return this.fileList.length > 0 && 
                   this.fieldMapping.company_name.sourceColumn && 
                   this.fieldMapping.city_name.sourceColumn &&
                   !this.importProgress.show
        }
    },
    watch: {
        selectedPlatform() {
            this.resetImport()
            this.setDefaultMapping()
        }
    },
    methods: {
        onFilesSelected(event) {
            const files = Array.from(event.target.files)
            if (files.length > 0) {
                this.processFiles(files)
            }
        },
        processFiles(files) {
            this.fileList = []
            files.forEach(file => {
                const fileInfo = {
                    file: file,
                    name: file.name,
                    size: file.size,
                    chunks: this.calculateChunks(file.size),
                    status: 'pending',
                    progress: 0,
                    progressText: 'Ожидает',
                    error: null
                }
                this.fileList.push(fileInfo)
            })
            
            // Парсим первый файл для предварительного просмотра
            if (files.length > 0) {
                this.parseFile(files[0])
            }
        },
        calculateChunks(fileSize) {
            const chunkSize = 10 * 1024 * 1024 // 10 МБ
            return Math.ceil(fileSize / chunkSize)
        },
        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes'
            const k = 1024
            const sizes = ['Bytes', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
        },
        getFileProgressVariant(status) {
            switch (status) {
                case 'pending': return 'bg-secondary'
                case 'uploading': return 'bg-primary'
                case 'completed': return 'bg-success'
                case 'error': return 'bg-danger'
                default: return 'bg-secondary'
            }
        },
        async parseFile(file) {
            try {
                this.importProgress = {
                    show: true,
                    percentage: 0,
                    message: 'Чтение файла...',
                    variant: 'bg-info'
                }

                const data = await this.readFile(file)
                this.previewData = data.slice(0, 5)
                this.previewColumns = Object.keys(data[0] || {})
                
                this.setDefaultMapping()
                this.importProgress.show = false
            } catch (error) {
                console.error('Ошибка при чтении файла:', error)
                this.$bvToast.toast('Ошибка при чтении файла', {
                    title: 'Ошибка',
                    variant: 'danger'
                })
                this.importProgress.show = false
            }
        },
        readFile(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = (e) => {
                    try {
                        let data
                        if (file.name.toLowerCase().endsWith('.csv')) {
                            // Для CSV файлов читаем как текст с правильной кодировкой
                            data = this.parseCSV(e.target.result)
                        } else {
                            // Для Excel файлов показываем ошибку - нужна библиотека для парсинга
                            throw new Error('Excel файлы пока не поддерживаются в предварительном просмотре. Используйте CSV формат.')
                        }
                        resolve(data)
                    } catch (error) {
                        reject(error)
                    }
                }
                reader.onerror = reject
                
                // Читаем CSV с правильной кодировкой
                if (file.name.toLowerCase().endsWith('.csv')) {
                    reader.readAsText(file, 'utf-8')
                } else {
                    // Для Excel файлов читаем как ArrayBuffer
                    reader.readAsArrayBuffer(file)
                }
            })
        },
        parseCSV(csvText) {
            // Улучшенный парсинг CSV с поддержкой кириллицы
            const lines = csvText.split(/\r?\n/).filter(line => line.trim())
            
            if (lines.length === 0) {
                throw new Error('Файл пуст или не содержит данных')
            }
            
            // Определяем разделитель (запятая, точка с запятой или табуляция)
            const firstLine = lines[0]
            let delimiter = ','
            if (firstLine.includes(';')) {
                delimiter = ';'
            } else if (firstLine.includes('\t')) {
                delimiter = '\t'
            }
            
            // Парсим заголовки
            const headers = this.parseCSVLine(lines[0], delimiter)
            const data = []
            
            // Парсим данные
            for (let i = 1; i < lines.length; i++) {
                if (lines[i].trim()) {
                    const values = this.parseCSVLine(lines[i], delimiter)
                    const row = {}
                    headers.forEach((header, index) => {
                        row[header] = values[index] || ''
                    })
                    data.push(row)
                }
            }
            
            return data
        },
        
        parseCSVLine(line, delimiter) {
            // Парсинг строки CSV с учетом кавычек
            const result = []
            let current = ''
            let inQuotes = false
            
            for (let i = 0; i < line.length; i++) {
                const char = line[i]
                
                if (char === '"') {
                    if (inQuotes && line[i + 1] === '"') {
                        // Экранированная кавычка
                        current += '"'
                        i++ // Пропускаем следующую кавычку
                    } else {
                        // Начало или конец кавычек
                        inQuotes = !inQuotes
                    }
                } else if (char === delimiter && !inQuotes) {
                    // Разделитель вне кавычек
                    result.push(current.trim())
                    current = ''
                } else {
                    current += char
                }
            }
            
            // Добавляем последнее поле
            result.push(current.trim())
            
            return result
        },
        setDefaultMapping() {
            // Автоматическое сопоставление полей для 2GIS
            if (this.selectedPlatform === '2gis') {
                this.fieldMapping.company_name.sourceColumn = this.findColumn(['name', 'company_name', 'название'])
                this.fieldMapping.inn.sourceColumn = this.findColumn(['inn', 'инн'])
                // В 2GIS телефоны часто находятся в колонке email
                this.fieldMapping.phone.sourceColumn = this.findColumn(['email', 'phone', 'телефон', 'phones'])
                // В 2GIS email часто находятся в колонке website
                this.fieldMapping.email.sourceColumn = this.findColumn(['website', 'email', 'почта'])
                // В 2GIS сайты часто находятся в колонке vkontakte
                this.fieldMapping.website.sourceColumn = this.findColumn(['vkontakte', 'website', 'сайт', 'url'])
                // В 2GIS адрес часто находится в колонке phone (геометрия)
                this.fieldMapping.address.sourceColumn = this.findColumn(['phone', 'geometry_post_code', 'address', 'адрес'])
                this.fieldMapping.city_name.sourceColumn = this.findColumn(['city_name', 'город', 'city'])
                this.fieldMapping.industry.sourceColumn = this.findColumn(['category', 'subcategory', 'отрасль'])
            }
        },
        findColumn(possibleNames) {
            for (const name of possibleNames) {
                // Точное совпадение
                let found = this.previewColumns.find(col => 
                    col.toLowerCase() === name.toLowerCase()
                )
                if (found) return found
                
                // Частичное совпадение
                found = this.previewColumns.find(col => 
                    col.toLowerCase().includes(name.toLowerCase())
                )
                if (found) return found
            }
            return null
        },
        getColumnLabel(column) {
            const labels = {
                'name': 'Название',
                'company_name': 'Название компании',
                'inn': 'ИНН',
                'phone': 'Телефон',
                'email': 'Email',
                'website': 'Сайт',
                'geometry_name': 'Адрес',
                'city_name': 'Город',
                'category': 'Категория',
                'subcategory': 'Подкатегория'
            }
            return labels[column] || column
        },
        async startImport() {
            if (!this.canStartImport) return

            this.isImportCancelled = false
            this.currentFileIndex = 0
            
            const totalFiles = this.fileList.length
            let totalResults = {
                total: 0,
                success: 0,
                errors: 0,
                skipped: 0,
                filtered: 0
            }

            try {
                this.importProgress = {
                    show: true,
                    percentage: 0,
                    message: `Начинаем импорт ${totalFiles} файл(ов)...`,
                    variant: 'bg-primary'
                }

                for (let i = 0; i < this.fileList.length; i++) {
                    if (this.isImportCancelled) {
                        this.importProgress.message = 'Импорт отменен'
                        this.importProgress.variant = 'bg-warning'
                        break
                    }

                    this.currentFileIndex = i
                    const fileInfo = this.fileList[i]
                    
                    // Обновляем статус файла
                    fileInfo.status = 'uploading'
                    fileInfo.progressText = 'Загружается...'
                    
                    this.importProgress.message = `Импорт файла ${i + 1} из ${totalFiles}: ${fileInfo.name}`

                    try {
                        const result = await this.importSingleFile(fileInfo)
                        
                        // Объединяем результаты
                        totalResults.total += result.total
                        totalResults.success += result.success
                        totalResults.errors += result.errors
                        totalResults.skipped += result.skipped || 0
                        totalResults.filtered += result.filtered || 0

                        // Обновляем статус файла
                        fileInfo.status = 'completed'
                        fileInfo.progress = 100
                        fileInfo.progressText = 'Завершен'

                    } catch (error) {
                        console.error(`Ошибка импорта файла ${fileInfo.name}:`, error)
                        fileInfo.status = 'error'
                        fileInfo.progressText = 'Ошибка'
                        fileInfo.error = error.message
                        totalResults.errors++
                    }

                    // Обновляем общий прогресс
                    const overallProgress = Math.round(((i + 1) / totalFiles) * 100)
                    this.importProgress.percentage = overallProgress
                }

                if (!this.isImportCancelled) {
                    this.importProgress = {
                        show: false,
                        percentage: 100,
                        message: 'Импорт завершен',
                        variant: 'bg-success'
                    }

                    this.importResults = {
                        title: 'Импорт успешно завершен',
                        alertClass: 'alert-success',
                        total: totalResults.total,
                        success: totalResults.success,
                        errors: totalResults.errors,
                        skipped: totalResults.skipped,
                        filtered: totalResults.filtered
                    }

                    this.$emit('imported', totalResults)
                    this.$bvToast.toast('Импорт завершен успешно', {
                        title: 'Успех',
                        variant: 'success'
                    })
                }

            } catch (error) {
                console.error('Общая ошибка импорта:', error)
                this.importProgress.show = false
                this.importResults = {
                    title: 'Ошибка при импорте',
                    alertClass: 'alert-danger',
                    total: totalResults.total,
                    success: totalResults.success,
                    errors: totalResults.errors + 1,
                    skipped: totalResults.skipped,
                    filtered: totalResults.filtered
                }
                this.$bvToast.toast('Ошибка при импорте данных', {
                    title: 'Ошибка',
                    variant: 'danger'
                })
            }
        },
        async importSingleFile(fileInfo) {
            const formData = new FormData()
            formData.append('file', fileInfo.file)
            formData.append('platform', this.selectedPlatform)
            formData.append('field_mapping', JSON.stringify(this.fieldMapping))
            formData.append('skip_duplicates', this.skipDuplicates)
            formData.append('validate_data', this.validateData)

            const response = await this.$api.coldClients.importFromFile(formData, {
                onUploadProgress: (progressEvent) => {
                    if (!this.isImportCancelled) {
                        const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                        fileInfo.progress = percentage
                        fileInfo.progressText = `Загрузка... ${percentage}%`
                    }
                }
            })

            return response.data
        },
        cancelImport() {
            this.isImportCancelled = true
            this.importProgress.message = 'Отмена импорта...'
            this.importProgress.variant = 'bg-warning'
            
            // Обновляем статус всех ожидающих файлов
            this.fileList.forEach((fileInfo, index) => {
                if (index > this.currentFileIndex && fileInfo.status === 'pending') {
                    fileInfo.status = 'cancelled'
                    fileInfo.progressText = 'Отменен'
                }
            })
        },
        resetImport() {
            this.selectedFiles = null
            this.fileList = []
            this.previewData = []
            this.previewColumns = []
            this.importProgress.show = false
            this.importResults = null
            this.isImportCancelled = false
            this.currentFileIndex = 0
            Object.keys(this.fieldMapping).forEach(key => {
                this.fieldMapping[key].sourceColumn = null
            })
        }
    }
}
</script>

<style scoped>
.import-content {
    padding: 1rem;
    height: 100%;
    overflow-y: auto;
}

.form-section {
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #eee;
}

.form-section:last-child {
    border-bottom: none;
}

.section-title {
    color: #495057;
    font-weight: 600;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #0d6348;
}

.preview-section {
    margin-top: 1rem;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 0.25rem;
}

.field-mapping {
    margin-bottom: 1rem;
}

.import-settings {
    margin-top: 1rem;
}

.import-settings .form-check {
    margin-bottom: 0.5rem;
}

.form-actions-bottom {
    position: absolute;
    width: 100%;
    padding: 20px 25px;
    left: 0;
    bottom: 0;
    background: #fff;
    border-top: 1px solid #eee;
    text-align: right;
}

.table-responsive {
    max-height: 300px;
    overflow-y: auto;
}

.file-list {
    margin-top: 1rem;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 0.25rem;
}

.file-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border-bottom: 1px solid #dee2e6;
}

.file-item:last-child {
    border-bottom: none;
}

.file-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.file-name {
    font-weight: 500;
}

.file-size {
    color: #6c757d;
    font-size: 0.875rem;
}

.file-chunks {
    color: #007bff;
    font-size: 0.75rem;
    background-color: #e3f2fd;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
}

.files-progress {
    margin-top: 1rem;
    padding: 1rem;
    background-color: #f8f9fa;
    border-radius: 0.25rem;
}

.file-progress-item {
    margin-bottom: 0.75rem;
}

.file-progress-item:last-child {
    margin-bottom: 0;
}

.file-progress-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;
}

.file-progress-text {
    font-size: 0.875rem;
    color: #6c757d;
}

.file-progress-bar {
    height: 0.5rem;
}

.file-progress-bar .progress-bar {
    font-size: 0.75rem;
    line-height: 0.5rem;
}
</style>
